<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { usePage } from '@inertiajs/vue3'
import RentalStatusPill from '@/Components/UI/RentalStatusPill.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'

const { t } = useI18n()
const page = usePage()
const locale = computed(() => page.props.app_setting?.locale || 'en-US')

const props = defineProps({
  rentals:     { type: Array,  required: true },
  maintenance: { type: Array,  required: true },
  vehicles:    { type: Array,  required: true },
  dayBuckets:  { type: Object, required: true },
})

const vehicleLookup = computed(() => Object.fromEntries(props.vehicles.map(v => [v.id, v])))
const rentalLookup  = computed(() => Object.fromEntries(props.rentals.map(r => [r.id, r])))
const maintenanceLookup = computed(() => Object.fromEntries(props.maintenance.map(m => [m.id, m])))

const pickupRows  = computed(() => props.dayBuckets.pickups.map(id => rentalLookup.value[id]).filter(Boolean))
const returnRows  = computed(() => props.dayBuckets.returns.map(id => rentalLookup.value[id]).filter(Boolean))
const activeRows  = computed(() => props.dayBuckets.active_through.map(id => rentalLookup.value[id]).filter(Boolean))
const maintStarts = computed(() => props.dayBuckets.maintenance_starts.map(id => maintenanceLookup.value[id]).filter(Boolean))
const maintEnds   = computed(() => props.dayBuckets.maintenance_ends.map(id => maintenanceLookup.value[id]).filter(Boolean))

const vehicleLabel = (id) => {
  const v = vehicleLookup.value[id]
  if (!v) return ''
  return v.registration ? `${v.label} · ${v.registration}` : v.label
}

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString(locale.value, { hour: '2-digit', minute: '2-digit' })
</script>

<template>
  <div class="space-y-6">
    <!-- Pickups -->
    <section>
      <h3 class="text-xs uppercase tracking-wider text-[color:var(--muted)] mb-2">{{ t('calendar.day.pickups') }}</h3>
      <EmptyState v-if="pickupRows.length === 0" type="success" :title="t('calendar.empty.pickups')" />
      <ul v-else class="divide-y divide-[color:var(--line)] rounded-[var(--radius-table)] border border-[color:var(--line)] bg-[color:var(--surface)]">
        <li v-for="r in pickupRows" :key="r.id">
          <Link :href="r.href" class="flex items-center gap-3 px-3 py-2 hover:bg-[color:var(--surfaceAlt)]">
            <span class="text-sm text-[color:var(--ink)] flex-1 truncate">{{ vehicleLabel(r.vehicle_id) }}</span>
            <span class="text-sm text-[color:var(--inkSoft)] flex-1 truncate">{{ r.customer_name }}</span>
            <span class="fw-value tabular-nums text-sm text-[color:var(--ink)] w-16 text-right">{{ formatTime(r.bar_start_at) }}</span>
            <RentalStatusPill :status="r.overdue ? 'overdue' : r.status" />
          </Link>
        </li>
      </ul>
    </section>

    <!-- Returns -->
    <section>
      <h3 class="text-xs uppercase tracking-wider text-[color:var(--muted)] mb-2">{{ t('calendar.day.returns') }}</h3>
      <EmptyState v-if="returnRows.length === 0" type="success" :title="t('calendar.empty.returns')" />
      <ul v-else class="divide-y divide-[color:var(--line)] rounded-[var(--radius-table)] border border-[color:var(--line)] bg-[color:var(--surface)]">
        <li v-for="r in returnRows" :key="r.id">
          <Link :href="r.href" class="flex items-center gap-3 px-3 py-2 hover:bg-[color:var(--surfaceAlt)]">
            <span class="text-sm text-[color:var(--ink)] flex-1 truncate">{{ vehicleLabel(r.vehicle_id) }}</span>
            <span class="text-sm text-[color:var(--inkSoft)] flex-1 truncate">{{ r.customer_name }}</span>
            <span class="fw-value tabular-nums text-sm text-[color:var(--ink)] w-16 text-right">{{ formatTime(r.bar_end_at) }}</span>
            <RentalStatusPill :status="r.overdue ? 'overdue' : r.status" />
          </Link>
        </li>
      </ul>
    </section>

    <!-- Active throughout the day -->
    <section>
      <h3 class="text-xs uppercase tracking-wider text-[color:var(--muted)] mb-2">{{ t('calendar.day.active') }}</h3>
      <EmptyState v-if="activeRows.length === 0" type="success" :title="t('calendar.empty.active')" />
      <ul v-else class="divide-y divide-[color:var(--line)] rounded-[var(--radius-table)] border border-[color:var(--line)] bg-[color:var(--surface)]">
        <li v-for="r in activeRows" :key="r.id">
          <Link :href="r.href" class="flex items-center gap-3 px-3 py-2 hover:bg-[color:var(--surfaceAlt)]">
            <span class="text-sm text-[color:var(--ink)] flex-1 truncate">{{ vehicleLabel(r.vehicle_id) }}</span>
            <span class="text-sm text-[color:var(--inkSoft)] flex-1 truncate">{{ r.customer_name }}</span>
            <RentalStatusPill :status="r.overdue ? 'overdue' : r.status" />
          </Link>
        </li>
      </ul>
    </section>

    <!-- Maintenance -->
    <section>
      <h3 class="text-xs uppercase tracking-wider text-[color:var(--muted)] mb-2">{{ t('calendar.day.maintenance') }}</h3>
      <EmptyState v-if="maintStarts.length === 0 && maintEnds.length === 0" type="success" :title="t('calendar.empty.maintenance')" />
      <ul v-else class="divide-y divide-[color:var(--line)] rounded-[var(--radius-table)] border border-[color:var(--line)] bg-[color:var(--surface)]">
        <li v-for="m in [...maintStarts, ...maintEnds]" :key="m.id">
          <Link :href="m.href" class="flex items-center gap-3 px-3 py-2 hover:bg-[color:var(--surfaceAlt)]">
            <span class="text-sm text-[color:var(--ink)] flex-1 truncate">{{ vehicleLabel(m.vehicle_id) }}</span>
            <span class="text-xs uppercase tracking-wider text-[color:var(--warn)]">{{ t('calendar.maintenance') }}</span>
          </Link>
        </li>
      </ul>
    </section>
  </div>
</template>
